"use client";

import { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import QRCode from "qrcode";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getDevAdvancedCopy } from "@/lib/dev-advanced-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function QrGenerator() {
  const [text, setText] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [size, setSize] = useState(300);
  const [errorLevel, setErrorLevel] = useState<"L" | "M" | "Q" | "H">("M");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getDevAdvancedCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "qr-generator") ?? tools[0],
    locale
  );

  const generateQR = async () => {
    if (!text.trim()) return;

    try {
      const url = await QRCode.toDataURL(text, {
        width: size,
        margin: 2,
        errorCorrectionLevel: errorLevel,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error("Error generating QR code:", err);
    }
  };

  const downloadQR = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `qrcode-${Date.now()}.png`;
    link.click();
  };

  useEffect(() => {
    if (text) {
      generateQR();
    }
  }, [text, size, errorLevel]);

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:qrcode" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        <div>
          <label className="mb-3 block text-lg font-medium text-positivus-dark">{copy.textOrUrl}</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={copy.enterTextOrUrl}
            className="min-h-[120px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all text-lg"
          />
        </div>

        <div className="grid gap-8 sm:grid-cols-2">
          <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
            <div className="flex justify-between items-center mb-4">
                <label className="text-lg font-medium text-positivus-dark">{copy.sizePx}</label>
                <span className="font-bold text-positivus-green bg-positivus-green/10 px-3 py-1 rounded-[10px]">{size}px</span>
            </div>
            <input
              type="range"
              min="200"
              max="600"
              step="50"
              value={size}
              onChange={(e) => setSize(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-positivus-dark"
            />
            <div className="flex justify-between text-xs font-bold text-gray-400 mt-2">
                 <span>{copy.small} (200px)</span>
                 <span>{copy.large} (600px)</span>
            </div>
          </div>

          <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
            <label className="mb-3 block text-lg font-medium text-positivus-dark">{copy.errorCorrection}</label>
            <div className="relative">
                <select
                value={errorLevel}
                onChange={(e) => setErrorLevel(e.target.value as "L" | "M" | "Q" | "H")}
                className="w-full appearance-none rounded-[15px] border-2 border-positivus-dark/20 bg-gray-50 px-5 py-3 text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all font-bold cursor-pointer"
                >
                <option value="L">{copy.low} (7%)</option>
                <option value="M">{copy.medium} (15%)</option>
                <option value="Q">{copy.quartile} (25%)</option>
                <option value="H">{copy.high} (30%)</option>
                </select>
                <Icon icon="mdi:chevron-down" className="absolute right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-positivus-dark pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {qrCodeUrl ? (
        <div className="flex flex-col items-center space-y-6 pt-4">
          <div className="rounded-[30px] border-4 border-positivus-dark bg-white p-8 shadow-[0_8px_0_0_#191A23]">
            <img src={qrCodeUrl} alt={localizedTool.name} className="mx-auto" style={{ width: size, height: size, maxWidth: '100%' }} />
          </div>

          <button
            onClick={downloadQR}
            className="flex items-center gap-3 rounded-[20px] bg-positivus-green px-8 py-4 text-xl font-bold text-positivus-dark hover:bg-positivus-dark hover:text-white transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
          >
            <Icon icon={uiIcons.download} className="h-6 w-6" />
            {copy.downloadQrCode}
          </button>
        </div>
      ) : (
         text && (
            <div className="flex h-60 items-center justify-center rounded-[30px] border-2 border-dashed border-positivus-dark/20 bg-gray-50">
                <div className="text-center">
                    <Icon icon="mdi:qrcode-scan" className="h-12 w-12 text-gray-400 mx-auto mb-2 animate-pulse" />
                    <p className="text-lg font-medium text-gray-500">{copy.generating}</p>
                </div>
            </div>
         )
      )}
    </div>
  );
}
